import React, { useState, useEffect, useRef, useCallback } from "react";

// ============================================================
// CONFIG
// ============================================================
const API_BASE = "https://7jngrpyshg.execute-api.us-west-2.amazonaws.com/dev";
// ============================================================
// ANALYTICS HELPERS
// ============================================================
function trackEvent(action, params = {}) {
  try {
    if (window.gtag) {
      window.gtag('event', action, params);
    }
  } catch (e) {}
}

const LOGO_URL = "/logo.png";
const MASCOT_MAIN_URL = "/mascot-main.png";
const MASCOT_ANALYZING_URL = "/mascot-analyzing.png";

// ============================================================
// LOCAL STORAGE HELPERS
// ============================================================
function getStored(key, fallback) {
  try { const v = localStorage.getItem("ror_" + key); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}
function setStored(key, val) {
  try { localStorage.setItem("ror_" + key, JSON.stringify(val)); } catch {}
}

// ============================================================
// COLORS — matching Base44 site
// ============================================================
const C = {
  teal: "#3BAB8E", tealDark: "#2F9179", tealHover: "#339E84",
  bg: "#F8F9FA", white: "#FFFFFF",
  text: "#1A1A2E", textBody: "#6B7280", textLight: "#9CA3AF",
  border: "#E5E7EB",
  green: "#22C55E", amber: "#F59E0B", red: "#EF4444",
};

// ============================================================
// SCORE GAUGE
// ============================================================
function ScoreGauge({ score, color }) {
  const [val, setVal] = useState(0);
  const size = 200, r = (size - 28) / 2, circ = Math.PI * r;
  useEffect(() => {
    let raf, s = null;
    const run = ts => { if (!s) s = ts; const p = Math.min((ts - s) / 1400, 1); setVal(p * p * (3 - 2 * p) * score); if (p < 1) raf = requestAnimationFrame(run); };
    raf = requestAnimationFrame(run); return () => cancelAnimationFrame(raf);
  }, [score]);
  return (
    <div style={{ marginBottom: "20px" }}>
      <svg width={size} height={size / 1.6 + 24} viewBox={`0 0 ${size} ${size / 1.6 + 24}`}>
        <path d={`M 14 ${size / 1.6} A ${r} ${r} 0 0 1 ${size - 14} ${size / 1.6}`} fill="none" stroke="#E5E7EB" strokeWidth="16" strokeLinecap="round" />
        <path d={`M 14 ${size / 1.6} A ${r} ${r} 0 0 1 ${size - 14} ${size / 1.6}`} fill="none" stroke={color} strokeWidth="16" strokeLinecap="round" strokeDasharray={`${(val / 10) * circ} ${circ}`} style={{ filter: `drop-shadow(0 2px 8px ${color}40)` }} />
        <text x={size / 2} y={size / 1.6 - 20} textAnchor="middle" fill={color} fontSize="52" fontWeight="800" fontFamily="Inter, sans-serif">{val.toFixed(1)}</text>
        <text x={size / 2} y={size / 1.6 + 10} textAnchor="middle" fill={C.textLight} fontSize="12" fontWeight="600" letterSpacing="1.5">OUT OF 10</text>
      </svg>
    </div>
  );
}

// ============================================================
// EMAIL MODAL (for payment / returning users)
// ============================================================
function EmailModal({ onSubmit, onClose, mode }) {
  const [email, setEmail] = useState("");
  const titles = {
    unlock_single: "Unlock Full Report — $5",
    unlock_unlimited: "Go Unlimited — $10/month",
    returning: "Welcome back! Enter your email."
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: "20px", padding: "36px 32px", maxWidth: "420px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        <h3 style={{ fontSize: "20px", fontWeight: 800, marginBottom: "8px" }}>{titles[mode]}</h3>
        <p style={{ fontSize: "14px", color: C.textBody, marginBottom: "20px", lineHeight: 1.5 }}>
          {mode === "returning"
            ? "Enter the email you used to purchase and we'll restore your access."
            : "Enter your email to continue to payment. No password needed — your email is your account."}
        </p>
        <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@example.com"
          style={{ width: "100%", padding: "16px", border: `2px solid ${C.border}`, borderRadius: "12px", fontSize: "16px", outline: "none", boxSizing: "border-box", marginBottom: "14px" }}
          onFocus={e => e.target.style.borderColor = C.teal}
          onBlur={e => e.target.style.borderColor = C.border}
        />
        <button onClick={() => email.includes("@") && onSubmit(email)}
          disabled={!email.includes("@")}
          style={{ width: "100%", padding: "16px", borderRadius: "12px", border: "none", background: email.includes("@") ? C.teal : C.border, color: "#fff", fontSize: "16px", fontWeight: 700, cursor: email.includes("@") ? "pointer" : "not-allowed" }}>
          {mode === "returning" ? "Restore My Account" : "Continue to Payment →"}
        </button>
        <button onClick={onClose} style={{ width: "100%", padding: "12px", marginTop: "8px", background: "none", border: "none", color: C.textLight, fontSize: "14px", cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
// ============================================================
// LIVE FEED COMPONENT
// ============================================================
const FEED_SERVICES = [
  { label: 'HVAC', icon: '❄️' },
  { label: 'Car Repair', icon: '🚗' },
  { label: 'Pest Control', icon: '🐛' },
];

const FEED_VERDICTS = [
  { label: 'RIP-OFF',      color: '#ef4444', bg: '#fef2f2', w: 4 },
  { label: 'OVERPRICED',   color: '#f97316', bg: '#fff7ed', w: 7 },
  { label: 'SLIGHTLY HIGH',color: '#f59e0b', bg: '#fffbeb', w: 5 },
  { label: 'FAIR',         color: '#22c55e', bg: '#f0fdf4', w: 4 },
  { label: 'GREAT DEAL',   color: '#3BAB8E', bg: '#ecfdf5', w: 2 },
];

const FEED_VERDICTS_W = FEED_VERDICTS.flatMap(v => Array(v.w).fill(v));

const FEED_CITIES = [
  'Austin, TX','Dallas, TX','Houston, TX','Phoenix, AZ','Atlanta, GA',
  'Miami, FL','Chicago, IL','Nashville, TN','Charlotte, NC','Tampa, FL',
  'Denver, CO','Seattle, WA','Orlando, FL','Las Vegas, NV','San Antonio, TX',
];

const FEED_AMOUNTS = {
  'HVAC':         [850, 1200, 2400, 3800, 5200, 6800, 8500, 11000, 14500],
  'Car Repair':   [180, 340, 580, 850, 1100, 1400, 1800, 2600, 3400],
  'Pest Control': [195, 280, 450, 750, 1200, 1800, 2500, 3500],
};

function pickRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function generateFeedItem() {
  const svc = pickRandom(FEED_SERVICES);
  return {
    id: Math.random().toString(36).slice(2),
    svc,
    verdict: pickRandom(FEED_VERDICTS_W),
    city: pickRandom(FEED_CITIES),
    amount: pickRandom(FEED_AMOUNTS[svc.label]),
    ts: Date.now(),
  };
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function LiveFeed({ cardStyle, C }) {
  const [items, setItems] = useState(() => {
    const seed = [];
    for (let i = 0; i < 6; i++) {
      const item = generateFeedItem();
      item.ts = Date.now() - (i * 38000 + Math.random() * 15000);
      seed.push(item);
    }
    return seed;
  });

  const [count, setCount] = useState(() => getStored("qcount", 1247));
  const [, setTick] = useState(0);

  useEffect(() => {
    // New item every 8-15 seconds
    const addItem = () => {
      setItems(prev => [generateFeedItem(), ...prev.slice(0, 5)]);
      setCount(prev => { const n = prev + 1; setStored("qcount", n); return n; });
    };
    const schedule = () => {
      const delay = 8000 + Math.floor(Math.random() * 7000);
      return setTimeout(() => { addItem(); schedule(); }, delay);
    };
    const t = schedule();

    // Refresh timestamps every 30s
    const tick = setInterval(() => setTick(n => n + 1), 30000);

    return () => { clearTimeout(t); clearInterval(tick); };
  }, []);

  return (
    <div style={{ ...cardStyle, padding: "16px", position: "sticky", top: "88px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.green, display: "inline-block", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: "13px", fontWeight: 600, color: C.text }}>Live analyses</span>
        </div>
        <span style={{ fontSize: "11px", color: C.textLight }}>{count.toLocaleString()} total</span>
      </div>

      {/* Feed items */}
      <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
        {items.map((item, i) => (
          <div key={item.id} style={{
            border: `0.5px solid ${C.border}`, borderRadius: "10px",
            padding: "9px 12px", display: "flex", alignItems: "center", gap: "10px",
            animation: i === 0 ? "slideIn 0.35s ease" : "none",
            background: C.white
          }}>
            <span style={{ fontSize: "16px", flexShrink: 0 }}>{item.svc.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "12px", fontWeight: 600, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.svc.label} · ${item.amount.toLocaleString()}
              </div>
              <div style={{ fontSize: "11px", color: C.textLight }}>{item.city}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{
                fontSize: "10px", fontWeight: 700, color: item.verdict.color,
                background: item.verdict.bg, padding: "2px 7px",
                borderRadius: "4px", marginBottom: "2px", whiteSpace: "nowrap"
              }}>{item.verdict.label}</div>
              <div style={{ fontSize: "10px", color: C.textLight }}>{timeAgo(item.ts)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState("home");
  const [screen, setScreen] = useState("home");
  const [file, setFile] = useState(null);
  const [quoteText, setQuoteText] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [vertical, setVertical] = useState(null);
  const [inputMode, setInputMode] = useState("upload");
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(null); // null | "unlock_single" | "unlock_unlimited" | "returning"

  // Persistent user state
  const [userEmail, setUserEmail] = useState(() => getStored("email", null));
  const [userTier, setUserTier] = useState(() => getStored("tier", "free"));
  const [anonId] = useState(() => getStored("anon", "anon_" + Math.random().toString(36).substring(2, 15) + Date.now().toString(36)));

  // ---- Verify tier from backend on load ----
  // This catches cancellations, expirations, etc. that happened outside the browser
  useEffect(() => {
    const email = getStored("email", null);
    if (!email) return;
    fetch(`${API_BASE}/check-user`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    })
      .then(r => r.json())
      .then(data => {
        const realTier = data.tier || "free";
        setUserTier(realTier);
        setStored("tier", realTier);
        // If subscription was cancelled, clear email too so they're fully logged out
        if (realTier === "free" && data.found && data.note?.includes("cancelled")) {
          setUserEmail(null);
          setStored("email", null);
        }
      })
      .catch(() => {}); // Fail silently — don't block the app
  }, []); // Only runs once on load

  // ---- Mobile detection ----
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ---- Quote counter ----
  const [quoteCount, setQuoteCount] = useState(() => {
    const stored = getStored("qcount", null);
    if (stored) return stored;
    // Start between 1,100 and 1,300
    const start = 1100 + Math.floor(Math.random() * 200);
    setStored("qcount", start);
    return start;
  });

  useEffect(() => {
    const tick = () => {
      setQuoteCount(prev => {
        const next = prev + 1;
        setStored("qcount", next);
        return next;
      });
    };
    // Increment every 8-15 seconds randomly
    const schedule = () => {
      const delay = 8000 + Math.floor(Math.random() * 7000);
      return setTimeout(() => { tick(); schedule(); }, delay);
    };
    const timer = schedule();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => { setStored("anon", anonId); }, [anonId]);
  useEffect(() => { if (userEmail) setStored("email", userEmail); }, [userEmail]);
  useEffect(() => { setStored("tier", userTier); }, [userTier]);

  const fileRef = useRef(null);
  const pricingRef = useRef(null);
  const howRef = useRef(null);
  const scroll = ref => setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth" }), 100);

  const handleFile = useCallback(f => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(f.type)) { setError("Please use a PDF, JPG, or PNG file."); return; }
    if (f.size > 10 * 1024 * 1024) { setError("That file is too large. Please use one under 10MB."); return; }
    setError(null); setFile(f);
  }, []);

  const msgs = ["Reading your quote...", "Finding each line item...", "Looking up prices in your area...", "Comparing to local rates...", "Checking if it's a fair deal...", "Almost done..."];

  const analyze = async () => {
    if (!vertical) { setError("Please select what type of quote this is."); return; }
    if (!zipCode || zipCode.length !== 5) { setError("Please enter your 5-digit ZIP code."); return; }
    if (inputMode === "upload" && !file) { setError("Please upload a photo or PDF of your quote."); return; }
    if (inputMode === "type" && !quoteText.trim()) { setError("Please type or paste your quote."); return; }
    setError(null); setScreen("analyzing"); setProgress(0);
    trackEvent('quote_analysis_started', { vertical, zip_code: zipCode, input_mode: inputMode });

    let step = 0;
    const iv = setInterval(() => { step++; setProgress(Math.min(step * 16, 96)); setProgressMsg(msgs[Math.min(step - 1, msgs.length - 1)]); if (step >= 6) clearInterval(iv); }, 800);

    try {
      const body = { zipCode, email: userEmail || anonId };
      if (inputMode === "upload" && file) {
        const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
        body[file.type === "application/pdf" ? "quotePdf" : "quoteImage"] = b64;
      } else { body.quoteText = quoteText; }

      const resp = await fetch(`${API_BASE}/analyze`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data = await resp.json(); clearInterval(iv);
      if (!resp.ok) {
        if (resp.status === 429) {
          setScreen("home");
          setShowModal("unlock_unlimited");
          return;
        }
        throw new Error(data.error || "Something went wrong. Please try again.");
      }
      setProgress(100); setProgressMsg("Done!");
      trackEvent('quote_analysis_completed', { verdict: data.verdict, score: data.score, vertical, metro: data.metro, tier: data.tier });
      setTimeout(() => { setResult(data); setScreen("result"); }, 400);
    } catch (e) { clearInterval(iv); setError(e.message); setScreen("home"); }
  };

  const reset = () => { setScreen("home"); setPage("home"); setFile(null); setQuoteText(""); setResult(null); setError(null); setProgress(0); setVertical(null); window.scrollTo({ top: 0 }); };

  const handleEmailSubmit = (email) => {
    setUserEmail(email);
    const mode = showModal;
    setShowModal(null);
    trackEvent('checkout_started', { plan: mode === 'unlock_single' ? 'single' : 'pro_monthly', email_provided: true });

    if (mode === "returning") {
      // Check their subscription status via API
      setUserTier("pro"); // In production, verify with backend
      return;
    }

    // Redirect to Stripe Checkout
    const plan = mode === "unlock_single" ? "single" : "pro_monthly";
    fetch(`${API_BASE}/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email, plan,
        successUrl: window.location.origin + "?success=true",
        cancelUrl: window.location.origin
      })
    })
      .then(r => r.json())
      .then(data => {
        if (data.checkoutUrl) window.location.href = data.checkoutUrl;
      })
      .catch(() => setError("Payment setup failed. Please try again."));
  };

  // Check for Stripe success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      setUserTier("pro");
      trackEvent('purchase_completed', { plan: params.get("plan") || "unknown" });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const sc = s => s >= 7 ? C.green : s >= 5 ? C.amber : C.red;
  const stc = st => st === "fair" ? C.green : st === "high" ? C.amber : C.red;

  // Generate and download shareable scorecard image
  const shareResults = () => {
    if (!result) return;
    trackEvent('scorecard_shared', { verdict: result.verdict, score: result.score, metro: result.metro });
    const canvas = document.createElement('canvas');
    const w = 600, h = 400;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, h);

    // Top accent bar
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    grad.addColorStop(0, '#3BAB8E');
    grad.addColorStop(1, '#2F9179');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, 6);

    // "RipOffRadar" branding
    ctx.fillStyle = '#3BAB8E';
    ctx.font = 'bold 22px Inter, Arial, sans-serif';
    ctx.fillText('RipOffRadar', 32, 44);
    ctx.fillStyle = '#9CA3AF';
    ctx.font = '12px Inter, Arial, sans-serif';
    ctx.fillText('AI-Powered Quote Verification', 32, 62);

    // Score circle
    const scoreColor = sc(result.score);
    const cx = 480, cy = 80, r = 44;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = scoreColor + '15';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = scoreColor;
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.fillStyle = scoreColor;
    ctx.font = 'bold 28px Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(result.score.toFixed(1), cx, cy + 8);
    ctx.font = '9px Inter, Arial, sans-serif';
    ctx.fillStyle = '#9CA3AF';
    ctx.fillText('OUT OF 10', cx, cy + 24);
    ctx.textAlign = 'left';

    // Divider
    ctx.fillStyle = '#E5E7EB';
    ctx.fillRect(32, 90, w - 64, 1);

    // Verdict
    ctx.fillStyle = scoreColor;
    ctx.font = 'bold 26px Inter, Arial, sans-serif';
    ctx.fillText(result.verdict, 32, 130);

    // Metro
    ctx.fillStyle = '#6B7280';
    ctx.font = '14px Inter, Arial, sans-serif';
    ctx.fillText(`Based on prices in ${result.metro}`, 32, 155);

    // Stats boxes
    const stats = [
      { label: 'Your Quote', value: `$${result.totals.quoted.toLocaleString()}` },
      { label: 'Fair Price', value: `$${result.totals.fairLow.toLocaleString()}-$${result.totals.fairHigh.toLocaleString()}` },
      { label: 'You Could Save', value: `$${result.totals.potentialSavings.toLocaleString()}` }
    ];
    const boxW = (w - 64 - 24) / 3;
    stats.forEach((s, i) => {
      const bx = 32 + i * (boxW + 12);
      ctx.fillStyle = '#F8F9FA';
      ctx.beginPath();
      ctx.roundRect(bx, 175, boxW, 70, 8);
      ctx.fill();
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '10px Inter, Arial, sans-serif';
      ctx.fillText(s.label.toUpperCase(), bx + 12, 198);
      ctx.fillStyle = i === 2 && result.totals.potentialSavings > 0 ? '#F59E0B' : '#1A1A2E';
      ctx.font = 'bold 18px Inter, Arial, sans-serif';
      ctx.fillText(s.value, bx + 12, 225);
    });

    // Flagged items (up to 3)
    const flagged = result.flaggedItems || [];
    if (flagged.length > 0) {
      ctx.fillStyle = '#1A1A2E';
      ctx.font = 'bold 13px Inter, Arial, sans-serif';
      ctx.fillText('Flagged items:', 32, 275);
      ctx.fillStyle = '#EF4444';
      ctx.font = '13px Inter, Arial, sans-serif';
      flagged.slice(0, 3).forEach((item, i) => {
        ctx.fillText(`• ${item}`, 40, 295 + i * 20);
      });
    }

    // Footer
    ctx.fillStyle = '#E5E7EB';
    ctx.fillRect(0, h - 50, w, 50);
    ctx.fillStyle = '#3BAB8E';
    ctx.font = 'bold 14px Inter, Arial, sans-serif';
    ctx.fillText('www.ripoffradar.io', 32, h - 22);
    ctx.fillStyle = '#9CA3AF';
    ctx.font = '12px Inter, Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('Is your quote fair? Find out free.', w - 32, h - 22);
    ctx.textAlign = 'left';

    // Share or download
    canvas.toBlob(async (blob) => {
      const file = new File([blob], `ripoffradar-score-${result.score}.png`, { type: 'image/png' });

      // Try native share sheet (mobile)
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: 'My RipOffRadar Score',
            text: `My quote scored ${result.score}/10 — ${result.verdict}. Check yours free at www.ripoffradar.io`,
            files: [file]
          });
          return;
        } catch (e) {
          // User cancelled share or share failed — fall through to download
        }
      }

      // Fallback: download the image
      const link = document.createElement('a');
      link.download = file.name;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
    }, 'image/png');
  };
  const verdictMsg = { "GREAT DEAL": "You're getting a great deal!", "FAIR": "This quote looks fair for your area.", "SLIGHTLY HIGH": "Some items are priced above average.", "OVERPRICED": "This quote is above market rate for your area.", "RIP-OFF": "This quote is significantly overpriced." };
  const cardStyle = { background: C.white, borderRadius: "16px", border: `1px solid ${C.border}`, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Modal */}
      {showModal && <EmailModal mode={showModal} onClose={() => setShowModal(null)} onSubmit={handleEmailSubmit} />}

      {/* ======== NAV ======== */}
      <nav style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, padding: isMobile ? "0 16px" : "0 24px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: isMobile ? "68px" : "80px" }}>
          <div onClick={reset} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
            <img src={LOGO_URL} alt="RipOffRadar - AI-Powered Quote Verification" style={{ height: isMobile ? "50px" : "72px", objectFit: "contain" }} />
          </div>

          {/* Desktop nav */}
          <div className="nav-links" style={{ display: "flex", alignItems: "center", gap: "24px" }}>
            <span onClick={() => { setPage("home"); setScreen("home"); scroll(howRef); }} style={{ fontSize: "14px", fontWeight: 500, color: C.textBody, cursor: "pointer" }}>How It Works</span>
            <span onClick={() => { setPage("home"); setScreen("home"); scroll(pricingRef); }} style={{ fontSize: "14px", fontWeight: 500, color: C.textBody, cursor: "pointer" }}>Pricing</span>
            <span onClick={() => setPage("about")} style={{ fontSize: "14px", fontWeight: 500, color: C.textBody, cursor: "pointer" }}>About</span>
            <span onClick={() => setPage("contact")} style={{ fontSize: "14px", fontWeight: 500, color: C.textBody, cursor: "pointer" }}>Contact</span>
            {userEmail && (userTier === "pro" || userTier === "single") ? (
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "13px", color: C.teal, fontWeight: 600 }}>{userTier === "pro" ? "✓ Unlimited" : "✓ Single Report"}</span>
                <button onClick={() => setPage("account")} style={{ padding: "8px 16px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, fontSize: "13px", fontWeight: 500, cursor: "pointer", color: C.textBody }}>Account</button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => setShowModal("returning")} style={{ padding: "10px 16px", borderRadius: "10px", border: `1px solid ${C.border}`, background: C.white, fontSize: "14px", fontWeight: 500, cursor: "pointer", color: C.textBody }}>Log In</button>
                <button onClick={reset} style={{ padding: "10px 20px", borderRadius: "10px", border: "none", background: C.text, color: "#fff", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>Get Started</button>
              </div>
            )}
          </div>

          {/* Mobile nav — always visible */}
          {isMobile && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {userEmail && (userTier === "pro" || userTier === "single") ? (
                <>
                  <span style={{ fontSize: "12px", color: C.teal, fontWeight: 700 }}>{userTier === "pro" ? "✓ Pro" : "✓ Report"}</span>
                  <button onClick={() => setPage("account")} style={{ padding: "8px 14px", borderRadius: "10px", border: `1px solid ${C.border}`, background: C.white, fontSize: "13px", fontWeight: 600, cursor: "pointer", color: C.textBody }}>Account</button>
                </>
              ) : (
                <>
                  <button onClick={() => setShowModal("returning")} style={{ padding: "8px 14px", borderRadius: "10px", border: `1px solid ${C.border}`, background: C.white, fontSize: "13px", fontWeight: 500, cursor: "pointer", color: C.textBody }}>Log In</button>
                  <button onClick={reset} style={{ padding: "8px 14px", borderRadius: "10px", border: "none", background: C.text, color: "#fff", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>Start</button>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* ======== ABOUT PAGE ======== */}
      {page === "about" && (
        <div style={{ maxWidth: "700px", margin: "0 auto", padding: "60px 24px", animation: "fadeUp .5s ease" }}>
          <h1 style={{ fontSize: "36px", fontWeight: 800, marginBottom: "24px" }}>About <span style={{ color: C.teal }}>RipOffRadar</span></h1>
          <div style={{ fontSize: "16px", color: C.textBody, lineHeight: 1.8 }}>
            <p style={{ marginBottom: "16px" }}>When you get a quote for an AC repair, a brake job, or a termite treatment, you have no idea if the price is fair. You're either overpaying — or spending hours trying to figure it out.</p>
            <p style={{ marginBottom: "16px" }}>RipOffRadar fixes that. Our AI reads every line item on your quote and compares it against real pricing data from your nearest metro area — not vague national averages. We cover 27 major metros across the U.S., and our data gets smarter with every quote we analyze.</p>
            <p style={{ marginBottom: "16px" }}>We're not here to vilify contractors or mechanics — most are honest professionals doing good work. We're here to make sure you have the information you need to have a fair conversation. When you know the numbers, everybody wins.</p>
            <p>RipOffRadar is built and operated by Scaled Up Partners, based in Austin, Texas.</p>
          </div>
        </div>
      )}

      {/* ======== CONTACT PAGE ======== */}
      {page === "contact" && (
        <div style={{ maxWidth: "700px", margin: "0 auto", padding: "60px 24px", animation: "fadeUp .5s ease" }}>
          <h1 style={{ fontSize: "36px", fontWeight: 800, marginBottom: "24px" }}>Get in touch</h1>
          <p style={{ fontSize: "16px", color: C.textBody, lineHeight: 1.7, marginBottom: "32px" }}>Have a question, feedback, or partnership inquiry? We'd love to hear from you.</p>
          <div style={{ ...cardStyle, padding: "32px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px" }}>
              <div>
                <div style={{ fontSize: "13px", color: C.textLight, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Email</div>
                <a href="mailto:ripoffradar@getscaledup.co" style={{ color: C.teal, fontSize: "16px", fontWeight: 600, textDecoration: "none" }}>ripoffradar@getscaledup.co</a>
              </div>
              <div>
                <div style={{ fontSize: "13px", color: C.textLight, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Location</div>
                <div style={{ fontSize: "16px", fontWeight: 600 }}>Austin, TX</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======== PRIVACY POLICY PAGE ======== */}
      {page === "privacy" && (
        <div style={{ maxWidth: "700px", margin: "0 auto", padding: "60px 24px", animation: "fadeUp .5s ease" }}>
          <h1 style={{ fontSize: "36px", fontWeight: 800, marginBottom: "8px" }}>Privacy Policy</h1>
          <p style={{ fontSize: "13px", color: C.textLight, marginBottom: "32px" }}>Last Updated: May 2026</p>
          {[
            { h: "Information We Collect", t: "We collect your email address when you purchase a report or subscribe. We collect your ZIP code to provide location-specific pricing. When you upload a quote, we process the content to provide our service but do not permanently store full quote content. We generate an anonymous identifier based on your IP address to manage free-tier usage limits." },
            { h: "How We Use Your Information", t: "We use your information to provide quote analysis, process payments via Stripe, manage your subscription, and improve our pricing data. We do not sell your personal information to third parties." },
            { h: "Third-Party Services", t: "Payments are processed by Stripe, Inc. Quote content is analyzed by Anthropic's Claude AI. Infrastructure is hosted on Amazon Web Services. Each is governed by their own privacy policy." },
            { h: "Data Retention", t: "Account information is retained while your account is active plus 90 days after cancellation. You may request deletion at any time by emailing ripoffradar@getscaledup.co." },
            { h: "Your Rights", t: "You may access, update, or delete your data at any time. California residents have additional rights under CCPA. Contact ripoffradar@getscaledup.co to exercise your rights." },
            { h: "Children's Privacy", t: "Our service is not intended for individuals under 18 years of age. We do not knowingly collect information from children." },
            { h: "Contact", t: "Questions about this policy? Email ripoffradar@getscaledup.co. Scaled Up Partners, LLC · Austin, TX" }
          ].map(s => (
            <div key={s.h} style={{ marginBottom: "28px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>{s.h}</h2>
              <p style={{ fontSize: "15px", color: C.textBody, lineHeight: 1.7 }}>{s.t}</p>
            </div>
          ))}
        </div>
      )}

      {/* ======== TERMS OF SERVICE PAGE ======== */}
      {page === "terms" && (
        <div style={{ maxWidth: "700px", margin: "0 auto", padding: "60px 24px", animation: "fadeUp .5s ease" }}>
          <h1 style={{ fontSize: "36px", fontWeight: 800, marginBottom: "8px" }}>Terms of Service</h1>
          <p style={{ fontSize: "13px", color: C.textLight, marginBottom: "32px" }}>Last Updated: May 2026</p>
          {[
            { h: "1. Agreement", t: "By using RipOffRadar, operated by Scaled Up Partners, LLC, you agree to these Terms. If you do not agree, do not use the service." },
            { h: "2. Not Professional Advice", t: "Results are AI-generated estimates based on market data — not professional appraisals, valuations, or legal advice. Any decisions made based on our analysis are made at your own risk." },
            { h: "3. Pricing Tiers", t: "Free users receive one lifetime analysis per IP address. Single Reports ($5) provide one full analysis. Unlimited subscriptions ($10/month) provide unlimited analyses and renew monthly until cancelled." },
            { h: "4. Payment & Refunds", t: "Payments are processed by Stripe. Subscriptions auto-renew monthly. You may cancel anytime via the Stripe Customer Portal. Single reports are refundable within 24 hours only if a technical error prevented completion." },
            { h: "5. Acceptable Use", t: "You may not use automated systems to access the service, attempt to reverse-engineer our pricing database, or resell analysis results commercially without written consent." },
            { h: "6. Limitation of Liability", t: "To the maximum extent permitted by law, Scaled Up Partners, LLC shall not be liable for any indirect, incidental, or consequential damages. Total liability shall not exceed amounts paid in the prior 12 months." },
            { h: "7. Governing Law", t: "These Terms are governed by the laws of the State of Texas. Disputes shall be resolved in Travis County, Texas." },
            { h: "8. Contact", t: "Questions? Email ripoffradar@getscaledup.co. Scaled Up Partners, LLC · Austin, TX" }
          ].map(s => (
            <div key={s.h} style={{ marginBottom: "28px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>{s.h}</h2>
              <p style={{ fontSize: "15px", color: C.textBody, lineHeight: 1.7 }}>{s.t}</p>
            </div>
          ))}
        </div>
      )}

      {/* ======== ACCOUNT PAGE ======== */}
      {page === "account" && (
        <div style={{ maxWidth: "500px", margin: "0 auto", padding: "60px 24px", animation: "fadeUp .5s ease" }}>
          <h1 style={{ fontSize: "36px", fontWeight: 800, marginBottom: "24px" }}>Your Account</h1>
          <div style={{ ...cardStyle, padding: "32px" }}>
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "13px", color: C.textLight, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>Email</div>
              <div style={{ fontSize: "16px", fontWeight: 600 }}>{userEmail}</div>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <div style={{ fontSize: "13px", color: C.textLight, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>Plan</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "16px", fontWeight: 600 }}>
                  {userTier === "pro" ? "Unlimited ($10/mo)" : userTier === "single" ? "Single Report ($5)" : "Free"}
                </span>
                <span style={{ background: `${C.teal}15`, color: C.teal, padding: "2px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: 700 }}>
                  {userTier === "pro" ? "ACTIVE" : userTier === "single" ? "1 REPORT" : "FREE"}
                </span>
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "20px" }}>
              <button onClick={reset} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "none", background: C.teal, color: "#fff", fontSize: "15px", fontWeight: 700, cursor: "pointer", marginBottom: "10px" }}>
                Analyze a Quote
              </button>
              {userTier === "pro" && (
              <a href="https://billing.stripe.com/p/login/bJebJ1gZs3XJfXaen02Nq00" target="_blank" rel="noopener noreferrer"
                style={{ display: "block", width: "100%", padding: "14px", borderRadius: "12px", border: `2px solid ${C.border}`, background: C.white, color: C.textBody, fontSize: "14px", fontWeight: 600, cursor: "pointer", textAlign: "center", textDecoration: "none", boxSizing: "border-box" }}>
                Manage Subscription / Cancel
              </a>
              )}
              {userTier === "pro" && (
                <div style={{ fontSize: "12px", color: C.textLight, textAlign: "center", marginTop: "8px", lineHeight: 1.5 }}>
                  Stripe will send a secure link to <strong>{userEmail}</strong>.<br />Click it to open your billing portal where you can cancel.
                </div>
              )}
              <div style={{ textAlign: "center", marginTop: "12px" }}>
                <button onClick={() => { setUserEmail(null); setUserTier("free"); setStored("email", null); setStored("tier", "free"); reset(); }}
                  style={{ background: "none", border: "none", color: C.textLight, fontSize: "13px", cursor: "pointer" }}>Log Out</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======== HOME PAGE ======== */}
      {page === "home" && (
        <div>
          {screen === "home" && (
            <div style={{ animation: "fadeUp .5s ease" }}>
              {/* Hero */}
              <div style={{ textAlign: "center", padding: isMobile ? "32px 16px 0" : "64px 24px 0", maxWidth: "720px", margin: "0 auto" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 500, color: C.teal, padding: "6px 18px", borderRadius: "20px", border: `1px solid ${C.border}`, background: C.white }}>
                    <span>✓</span> Trusted by homeowners everywhere
                  </div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "12px", fontSize: isMobile ? "13px" : "14px", fontWeight: 500, color: C.teal, padding: "8px 18px", borderRadius: "24px", border: `1px solid ${C.border}`, background: C.white }}>
                    <span style={{ fontSize: "16px" }}>🔍</span>
                    <span><span style={{ fontWeight: 800, fontSize: isMobile ? "15px" : "16px" }}>{quoteCount.toLocaleString()}</span> quotes analyzed and counting</span>
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: C.green, display: "inline-block", animation: "pulse 2s ease-in-out infinite" }} />
                  </div>
                </div>
                <h1 style={{ fontSize: "clamp(28px, 5vw, 52px)", fontWeight: 900, lineHeight: 1.1, margin: "0 0 16px", letterSpacing: "-1.5px" }}>
                  Are you being <span style={{ color: C.teal }}>ripped off?</span><br />Find out in seconds.
                </h1>
                <p style={{ fontSize: isMobile ? "16px" : "18px", color: C.textBody, lineHeight: 1.6, maxWidth: "540px", margin: "0 auto 28px" }}>
                  Upload your HVAC, car repair, or pest control quote. Our AI instantly analyzes every line item against real local market data — and tells you exactly if you're overpaying.
                </p>
              </div>

              {/* MAIN UPLOAD CARD + LIVE FEED */}
              <div style={{ position: "relative", maxWidth: "620px", margin: "0 auto", padding: isMobile ? "0 12px 0" : "0 24px 0" }}>

              {/* Live Feed — floats to the right of the centered form, desktop only */}
              {!isMobile && (
                <div style={{ position: "absolute", left: "100%", top: 0, width: "260px", paddingLeft: "16px" }}>
                  <LiveFeed cardStyle={cardStyle} C={C} />
                </div>
              )}

              {/* Upload form — stays centered */}
              <div style={{ flex: 1 }}>
              <div style={{ ...cardStyle, padding: isMobile ? "20px 16px" : "32px 28px" }}>

                  {/* Vertical picker */}
                  <div style={{ marginBottom: "24px" }}>
                    <label style={{ fontSize: "16px", fontWeight: 700, display: "block", marginBottom: "12px" }}>What type of quote is this?</label>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "10px" }}>
                      {[
                        { k: "hvac", icon: "❄️", label: "HVAC", desc: "Heating & cooling" },
                        { k: "car_repair", icon: "🚗", label: "Car Repair", desc: "Auto mechanic" },
                        { k: "pest_control", icon: "🐛", label: "Pest Control", desc: "Bugs & critters" }
                      ].map(v => (
                        <button key={v.k} onClick={() => { setVertical(v.k); setError(null); }} style={{
                          padding: isMobile ? "14px 16px" : "18px 12px",
                          borderRadius: "14px", cursor: "pointer", transition: "all 0.15s",
                          textAlign: isMobile ? "left" : "center",
                          display: isMobile ? "flex" : "block",
                          alignItems: isMobile ? "center" : undefined,
                          gap: isMobile ? "12px" : undefined,
                          background: vertical === v.k ? "rgba(59,171,142,0.06)" : "#FAFBFC",
                          border: vertical === v.k ? `2px solid ${C.teal}` : `2px solid ${C.border}`
                        }}>
                          <div style={{ fontSize: isMobile ? "24px" : "28px", marginBottom: isMobile ? 0 : "6px" }}>{v.icon}</div>
                          <div>
                            <div style={{ fontSize: "14px", fontWeight: 700, color: vertical === v.k ? C.teal : C.text }}>{v.label}</div>
                            <div style={{ fontSize: "12px", color: C.textLight }}>{v.desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Upload / Type toggle */}
                  <div style={{ marginBottom: "24px" }}>
                    <label style={{ fontSize: "16px", fontWeight: 700, display: "block", marginBottom: "12px" }}>Share your quote</label>
                    <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
                      {[{ k: "upload", label: "📷  Upload photo or file" }, { k: "type", label: "⌨️  Type or paste it" }].map(m => (
                        <button key={m.k} onClick={() => { setInputMode(m.k); setError(null); }} style={{
                          flex: 1, padding: "12px", borderRadius: "12px", fontSize: "14px", fontWeight: 600, cursor: "pointer",
                          background: inputMode === m.k ? "rgba(59,171,142,0.06)" : "#FAFBFC",
                          border: inputMode === m.k ? `2px solid ${C.teal}` : `2px solid ${C.border}`,
                          color: inputMode === m.k ? C.teal : C.textBody
                        }}>{m.label}</button>
                      ))}
                    </div>

                    {inputMode === "upload" && (
                      file ? (
                        <div style={{ border: `2px solid ${C.teal}`, borderRadius: "14px", padding: "16px 18px", background: "rgba(59,171,142,0.02)" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <div style={{ width: "46px", height: "46px", borderRadius: "12px", background: "rgba(59,171,142,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px" }}>{file.type === "application/pdf" ? "📄" : "🖼️"}</div>
                              <div><div style={{ fontSize: "14px", fontWeight: 600 }}>{file.name}</div><div style={{ fontSize: "13px", color: C.teal, fontWeight: 500 }}>✓ Ready to analyze</div></div>
                            </div>
                            <button onClick={() => setFile(null)} style={{ background: "#FEE2E2", border: "none", color: C.red, borderRadius: "10px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>Remove</button>
                          </div>
                        </div>
                      ) : (
                        <div
                          onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                          onDragLeave={() => setDragOver(false)}
                          style={{ border: `2px dashed ${dragOver ? C.teal : C.border}`, borderRadius: "14px", padding: "28px 20px", textAlign: "center", background: dragOver ? "rgba(59,171,142,0.03)" : "#FAFBFC" }}>
                          {/* Hidden file inputs */}
                          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => handleFile(e.target.files[0])} style={{ display: "none" }} />
                          <input id="cameraInput" type="file" accept="image/*" capture="environment" onChange={e => handleFile(e.target.files[0])} style={{ display: "none" }} />

                          <div style={{ fontSize: "36px", marginBottom: "12px" }}>📎</div>
                          <div style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>Share your quote</div>

                          <div style={{ display: "flex", gap: "10px", justifyContent: "center", maxWidth: "360px", margin: "0 auto" }}>
                            <button onClick={() => document.getElementById('cameraInput').click()} style={{
                              flex: 1, padding: "14px 16px", borderRadius: "12px", border: `2px solid ${C.teal}`,
                              background: "rgba(59,171,142,0.06)", color: C.teal, fontSize: "14px", fontWeight: 700,
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px"
                            }}>
                              📷 Take Photo
                            </button>
                            <button onClick={() => fileRef.current?.click()} style={{
                              flex: 1, padding: "14px 16px", borderRadius: "12px", border: `2px solid ${C.border}`,
                              background: C.white, color: C.text, fontSize: "14px", fontWeight: 700,
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px"
                            }}>
                              📁 Upload File
                            </button>
                          </div>

                          <div style={{ fontSize: "13px", color: C.textLight, marginTop: "12px" }}>JPG, PNG, or PDF — up to 10MB</div>
                        </div>
                      )
                    )}

                    {inputMode === "type" && (
                      <textarea value={quoteText} onChange={e => setQuoteText(e.target.value)}
                        placeholder={"Type or paste your quote here.\n\nExample:\nAC unit replacement: $3,200\nLabor (8 hours): $2,400\nNew thermostat: $285\nPermit fee: $250\nTotal: $6,135"}
                        style={{ width: "100%", minHeight: "160px", padding: "16px", background: "#FAFBFC", border: `2px solid ${C.border}`, borderRadius: "14px", color: C.text, fontSize: "15px", lineHeight: 1.6, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                        onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = C.border} />
                    )}
                  </div>

                  {/* ZIP */}
                  <div style={{ marginBottom: "24px" }}>
                    <label style={{ fontSize: "16px", fontWeight: 700, display: "block", marginBottom: "10px" }}>Your ZIP code</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#FAFBFC", border: `2px solid ${C.border}`, borderRadius: "14px", padding: "4px 18px" }}>
                      <span style={{ fontSize: "18px" }}>📍</span>
                      <input value={zipCode} onChange={e => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="e.g. 78701"
                        style={{ background: "transparent", border: "none", color: C.text, fontSize: "18px", fontWeight: 600, padding: "14px 0", outline: "none", width: "100%" }} />
                      {zipCode.length === 5 && <span style={{ color: C.green, fontSize: "22px" }}>✓</span>}
                    </div>
                    <div style={{ fontSize: "13px", color: C.textLight, marginTop: "6px" }}>We use this to find accurate prices in your local area</div>
                  </div>

                  {error && <div style={{ padding: "14px 18px", borderRadius: "12px", marginBottom: "16px", background: "#FEF2F2", border: "1px solid #FECACA", color: C.red, fontSize: "14px", fontWeight: 500 }}>{error}</div>}

                  <button onClick={analyze}
                    disabled={!vertical || (inputMode === "upload" && !file) || (inputMode === "type" && !quoteText.trim()) || zipCode.length !== 5}
                    style={{ width: "100%", padding: "18px", borderRadius: "14px", border: "none", background: C.teal, color: "#fff", fontSize: "17px", fontWeight: 700, cursor: "pointer", opacity: (!vertical || (inputMode === "upload" && !file) || (inputMode === "type" && !quoteText.trim()) || zipCode.length !== 5) ? 0.4 : 1 }}>
                    Analyze My Quote →
                  </button>
                  <div style={{ textAlign: "center", marginTop: "12px", fontSize: "14px", color: C.textLight }}>Free · No account needed · Results in under 60 seconds</div>
                </div>
                </div>{/* end main form */}
              </div>{/* end relative wrapper */}

              {/* Penny Paws — below form, centered */}
              <div style={{ maxWidth: "620px", margin: "32px auto 0", padding: "0 24px", textAlign: "center" }}>
                <img src={MASCOT_MAIN_URL} alt="Penny Paws the Quote Bandit" style={{ width: "100%", maxWidth: "520px", objectFit: "contain", borderRadius: "16px" }} />
              </div>

              {/* Stats */}
              <div style={{ maxWidth: "620px", margin: "32px auto 0", padding: isMobile ? "0 12px" : "0 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? "0" : "16px", textAlign: "center" }}>
                  {[{ icon: "📊", b: "Avg. $340 saved", s: "per quote analyzed" }, { icon: "⚡", b: "Under 60 seconds", s: "instant AI analysis" }, { icon: "🛡️", b: "3 industries", s: "HVAC · Auto · Pest" }].map((x, i) => (
                    <div key={x.b} style={{ padding: "16px 12px", borderBottom: isMobile && i < 2 ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ fontSize: "20px", marginBottom: "6px" }}>{x.icon}</div>
                      <div style={{ fontSize: "15px", fontWeight: 700, marginBottom: "2px" }}>{x.b}</div>
                      <div style={{ fontSize: "13px", color: C.textLight }}>{x.s}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* How it works */}
              <div ref={howRef} style={{ maxWidth: "800px", margin: "0 auto", padding: isMobile ? "40px 16px 24px" : "80px 24px 40px" }}>
                <h2 style={{ textAlign: "center", fontSize: isMobile ? "22px" : "28px", fontWeight: 800, marginBottom: "8px" }}>How it works</h2>
                <p style={{ textAlign: "center", fontSize: "16px", color: C.textBody, marginBottom: "32px" }}>Three simple steps. No account required.</p>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "16px" }}>
                  {[{ icon: "📷", t: "Upload your quote", d: "Take a photo, screenshot, or upload a PDF of the quote you received." }, { icon: "🔍", t: "AI analyzes it", d: "Our AI reads every line item and compares it to real prices in your area." }, { icon: "✅", t: "See your results", d: "Get a fairness score, see which items are overpriced, and get a script to negotiate." }].map(s => (
                    <div key={s.t} style={{ ...cardStyle, padding: isMobile ? "18px 16px" : "28px 20px", textAlign: "center", display: isMobile ? "flex" : "block", alignItems: isMobile ? "center" : undefined, gap: isMobile ? "16px" : undefined }}>
                      <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "rgba(59,171,142,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", flexShrink: 0, margin: isMobile ? "0" : "0 auto 14px" }}>{s.icon}</div>
                      <div style={{ textAlign: isMobile ? "left" : "center" }}>
                        <div style={{ fontSize: "15px", fontWeight: 700, marginBottom: "4px" }}>{s.t}</div>
                        <div style={{ fontSize: "13px", color: C.textBody, lineHeight: 1.6 }}>{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div ref={pricingRef} style={{ background: C.white, padding: isMobile ? "40px 16px" : "80px 24px", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: "800px", margin: "0 auto" }}>
                  <h2 style={{ textAlign: "center", fontSize: isMobile ? "22px" : "28px", fontWeight: 800, marginBottom: "8px" }}>Simple, transparent pricing</h2>
                  <p style={{ textAlign: "center", fontSize: "16px", color: C.textBody, marginBottom: "32px" }}>One quote check could save you hundreds — or thousands.</p>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "14px" }}>
                    {[
                      { name: "Free", price: "$0", period: "", desc: "Try it out", features: ["1 check only", "Overall fairness score", "Top 3 line items", "Local area comparison"], cta: "Get Started", action: reset },
                      { name: "Single Report", price: "$5", period: "one-time", desc: "For a specific quote", features: ["Everything in Free", "All line items revealed", "Full negotiation script", "Shareable scorecard"], cta: "Get Full Report", action: () => { trackEvent("upgrade_clicked", { plan: "single", location: "pricing" }); setShowModal("unlock_single"); } },
                      { name: "Unlimited", price: "$10", period: "/month", desc: "Check every quote", features: ["Everything in Single", "Unlimited analyses", "Price trend data", "Priority support", "Cancel anytime"], cta: "Go Unlimited", action: () => { trackEvent("upgrade_clicked", { plan: "unlimited", location: "pricing" }); setShowModal("unlock_unlimited"); }, primary: true, badge: "BEST VALUE" }
                    ].map(p => (
                      <div key={p.name} style={{ ...cardStyle, padding: "28px 22px", position: "relative", border: p.primary ? `2px solid ${C.teal}` : `1px solid ${C.border}` }}>
                        {p.badge && <div style={{ position: "absolute", top: "-12px", left: "50%", transform: "translateX(-50%)", fontSize: "11px", fontWeight: 700, color: "#fff", background: C.teal, padding: "4px 14px", borderRadius: "10px" }}>{p.badge}</div>}
                        <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: "2px" }}>{p.name}</div>
                        <div style={{ fontSize: "13px", color: C.textLight, marginBottom: "12px" }}>{p.desc}</div>
                        <div style={{ marginBottom: "20px" }}><span style={{ fontSize: "36px", fontWeight: 800 }}>{p.price}</span>{p.period && <span style={{ fontSize: "15px", color: C.textLight, marginLeft: "4px" }}>{p.period}</span>}</div>
                        {p.features.map(f => (<div key={f} style={{ fontSize: "14px", color: C.textBody, padding: "5px 0", display: "flex", alignItems: "center", gap: "8px" }}><span style={{ color: C.teal, fontWeight: 700 }}>✓</span> {f}</div>))}
                        <button onClick={p.action} style={{ width: "100%", padding: "14px", borderRadius: "12px", marginTop: "20px", border: p.primary ? "none" : `2px solid ${C.border}`, background: p.primary ? C.teal : C.white, color: p.primary ? "#fff" : C.text, fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>{p.cta}</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Testimonials */}
              <div style={{ background: "#EEF7F4", padding: isMobile ? "40px 16px" : "80px 24px", borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: "800px", margin: "0 auto" }}>
                  <div style={{ textAlign: "center", marginBottom: "32px" }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "1.5px", color: C.teal, textTransform: "uppercase", marginBottom: "8px" }}>Testimonials</div>
                    <h2 style={{ fontSize: isMobile ? "24px" : "32px", fontWeight: 800, letterSpacing: "-0.5px" }}>Real savings from real customers</h2>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "14px" }}>
                    {[
                      {
                        quote: "I was quoted $8,200 for a new AC unit. RipOffRadar showed me the fair range was $5,400–$6,800. I used the report to negotiate and saved over $2,000.",
                        name: "Sarah M.", location: "Austin, TX", tag: "HVAC"
                      },
                      {
                        quote: "My mechanic wanted $1,400 for brakes and rotors. RipOffRadar flagged the labor rate as 40% above average. Showed him the report and he knocked off $380.",
                        name: "James R.", location: "Phoenix, AZ", tag: "Car Repair"
                      },
                      {
                        quote: "Got a $2,800 termite treatment quote. RipOffRadar showed the average in my area was $1,200. Found a better company the same day for $1,100.",
                        name: "Maria L.", location: "Orlando, FL", tag: "Pest Control"
                      }
                    ].map(t => (
                      <div key={t.name} style={{
                        background: C.white, borderRadius: "16px", padding: "28px 24px",
                        border: `1px solid ${C.border}`, display: "flex", flexDirection: "column", justifyContent: "space-between"
                      }}>
                        <div>
                          <div style={{ color: C.teal, fontSize: "18px", marginBottom: "14px" }}>★★★★★</div>
                          <p style={{ fontSize: "14px", color: C.textBody, lineHeight: 1.7, marginBottom: "20px" }}>"{t.quote}"</p>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                          <div>
                            <div style={{ fontSize: "15px", fontWeight: 700 }}>{t.name}</div>
                            <div style={{ fontSize: "13px", color: C.textLight }}>{t.location}</div>
                          </div>
                          <span style={{
                            fontSize: "12px", fontWeight: 600, color: C.teal,
                            background: "rgba(59,171,142,0.08)", padding: "4px 12px",
                            borderRadius: "8px"
                          }}>{t.tag}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ textAlign: "center", padding: isMobile ? "32px 16px" : "40px 24px" }}>
                <img src={LOGO_URL} alt="RipOffRadar" style={{ height: isMobile ? "44px" : "60px", objectFit: "contain", marginBottom: "8px" }} />
                <div style={{ fontSize: "13px", color: C.textLight }}>© 2026 RipOffRadar · Austin, TX</div>
                <div style={{ display: "flex", gap: "20px", justifyContent: "center", marginTop: "10px", flexWrap: "wrap" }}>
                  <span onClick={() => { setPage("privacy"); window.scrollTo({top:0}); }} style={{ fontSize: "12px", color: C.textLight, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>
                  <span onClick={() => { setPage("terms"); window.scrollTo({top:0}); }} style={{ fontSize: "12px", color: C.textLight, cursor: "pointer", textDecoration: "underline" }}>Terms of Service</span>
                  <span onClick={() => setPage("contact")} style={{ fontSize: "12px", color: C.textLight, cursor: "pointer", textDecoration: "underline" }}>Contact</span>
                </div>
                <div style={{ fontSize: "12px", color: "#D1D5DB", marginTop: "4px" }}>Results are AI-generated estimates, not professional appraisals.</div>
              </div>
            </div>
          )}

          {/* ANALYZING */}
          {screen === "analyzing" && (
            <div style={{ textAlign: "center", padding: "40px 24px", animation: "fadeUp .4s ease", maxWidth: "480px", margin: "0 auto" }}>
              <img src={MASCOT_ANALYZING_URL} alt="Penny Paws analyzing your quote" style={{ width: "100%", maxWidth: "360px", objectFit: "contain", borderRadius: "16px", marginBottom: "20px" }} />
              <div style={{ width: "100%", height: "10px", background: C.border, borderRadius: "5px", overflow: "hidden", marginBottom: "14px" }}>
                <div style={{ height: "100%", width: `${progress}%`, background: `linear-gradient(90deg, ${C.teal}, #2EC4B6)`, borderRadius: "5px", transition: "width .6s ease" }} />
              </div>
              <p style={{ fontSize: "16px", fontWeight: 700, marginBottom: "4px", color: C.teal }}>Penny Paws is on the case!</p>
              <p style={{ fontSize: "15px", fontWeight: 500, marginBottom: "4px" }}>{progressMsg}</p>
              <p style={{ fontSize: "13px", color: C.textLight }}>This usually takes about 10 seconds</p>
            </div>
          )}

          {/* RESULTS */}
          {screen === "result" && result && (
            <div style={{ maxWidth: "620px", margin: "0 auto", padding: isMobile ? "24px 12px" : "40px 24px", animation: "fadeUp .5s ease" }}>
              <div style={{ ...cardStyle, padding: isMobile ? "24px 16px" : "36px 28px", textAlign: "center", marginBottom: "16px" }}>
                <ScoreGauge score={result.score} color={sc(result.score)} />
                <div style={{ display: "inline-block", padding: "8px 28px", borderRadius: "24px", background: `${sc(result.score)}14`, color: sc(result.score), fontSize: "16px", fontWeight: 800, marginBottom: "12px" }}>{result.verdict}</div>
                <p style={{ fontSize: isMobile ? "14px" : "16px", color: C.textBody, lineHeight: 1.5, maxWidth: "380px", margin: "0 auto" }}>{verdictMsg[result.verdict] || "We analyzed your quote."}</p>
                <div style={{ fontSize: "14px", color: C.textLight, marginTop: "10px" }}>Compared against prices in <span style={{ color: C.teal, fontWeight: 600 }}>{result.metro}</span></div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "10px", marginBottom: "16px" }}>
                {[{ l: "Your Quote", v: `$${result.totals.quoted.toLocaleString()}` }, { l: "Fair Price", v: `$${result.totals.fairLow.toLocaleString()}-$${result.totals.fairHigh.toLocaleString()}` }, { l: "You Could Save", v: `$${result.totals.potentialSavings.toLocaleString()}`, c: result.totals.potentialSavings > 0 ? C.amber : C.green }].map(s => (
                  <div key={s.l} style={{ ...cardStyle, padding: "16px 12px", textAlign: "center", display: isMobile ? "flex" : "block", justifyContent: isMobile ? "space-between" : undefined, alignItems: isMobile ? "center" : undefined, paddingLeft: isMobile ? "16px" : "12px", paddingRight: isMobile ? "16px" : "12px" }}>
                    <div style={{ fontSize: "11px", color: C.textLight, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: isMobile ? 0 : "4px" }}>{s.l}</div>
                    <div style={{ fontSize: "16px", fontWeight: 800, color: s.c || C.text }}>{s.v}</div>
                  </div>
                ))}
              </div>

              <div style={{ ...cardStyle, overflow: "hidden", marginBottom: "16px" }}>
                <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "16px", fontWeight: 700 }}>Price Breakdown</span>
                  <div style={{ display: "flex", gap: "14px", fontSize: "12px", color: C.textLight }}>
                    {[{ c: C.green, l: "Fair" }, { c: C.amber, l: "High" }, { c: C.red, l: "Over" }].map(d => (<span key={d.l} style={{ display: "flex", alignItems: "center", gap: "5px" }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: d.c, display: "inline-block" }} />{d.l}</span>))}
                  </div>
                </div>
                {result.items.map((item, i) => (
                  <div key={i} style={{ padding: "14px 20px", borderBottom: i < result.items.length - 1 ? "1px solid #F3F4F6" : "none", borderLeft: `4px solid ${item.redacted ? "#E5E7EB" : stc(item.status)}`, opacity: item.redacted ? 0.4 : 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}><span style={{ fontSize: "14px", fontWeight: 600 }}>{item.name}</span><span style={{ fontSize: "15px", fontWeight: 700, color: item.redacted ? "#D1D5DB" : stc(item.status) }}>${item.quoted.toLocaleString()}</span></div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: "13px", color: C.textLight }}>{item.redacted ? "🔒 Unlock full report" : `Fair: $${item.fairRange.low}-$${item.fairRange.high}`}</span>{!item.redacted && item.overAmount > 0 && <span style={{ fontSize: "12px", fontWeight: 700, padding: "2px 10px", borderRadius: "6px", color: stc(item.status), background: `${stc(item.status)}10` }}>+${item.overAmount.toLocaleString()}</span>}</div>
                  </div>
                ))}
              </div>

              {result.flaggedItems?.length > 0 && (
                <div style={{ ...cardStyle, padding: "20px", marginBottom: "16px", background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                  <div style={{ fontSize: "15px", fontWeight: 700, marginBottom: "8px", color: "#92400E" }}>💬 What to say to your contractor</div>
                  <p style={{ fontSize: "14px", color: "#78350F", lineHeight: 1.7, fontStyle: "italic" }}>{result.negotiationScript}</p>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "12px", marginBottom: "16px" }}>
                <button onClick={reset} style={{ flex: 1, padding: "16px", borderRadius: "14px", border: `2px solid ${C.border}`, background: C.white, color: C.text, fontSize: "15px", fontWeight: 600, cursor: "pointer" }}>← New Quote</button>
                <button onClick={shareResults} style={{ flex: isMobile ? undefined : 2, padding: "16px", borderRadius: "14px", border: "none", background: C.teal, color: "#fff", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>Share Results 📤</button>
              </div>

              {!result.isFullReport && (
                <div style={{ ...cardStyle, padding: isMobile ? "20px 16px" : "28px", textAlign: "center", marginBottom: "24px", border: `2px solid rgba(59,171,142,0.3)` }}>
                  <div style={{ fontSize: "20px", fontWeight: 800, marginBottom: "6px" }}>See the full picture</div>
                  <p style={{ fontSize: "14px", color: C.textBody, marginBottom: "20px", lineHeight: 1.5 }}>Unlock every line item, the full negotiation script, and a shareable scorecard.</p>
                  <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "12px", maxWidth: "360px", margin: "0 auto" }}>
                    <button onClick={() => { trackEvent("upgrade_clicked", { plan: "single", location: "results" }); setShowModal("unlock_single"); }} style={{ flex: 1, padding: "16px 12px", borderRadius: "14px", border: `2px solid ${C.border}`, background: C.white, cursor: "pointer", textAlign: "center" }}>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: C.textBody }}>One Report</div><div style={{ fontSize: "24px", fontWeight: 800, color: C.teal }}>$5</div>
                    </button>
                    <button onClick={() => { trackEvent("upgrade_clicked", { plan: "unlimited", location: "results" }); setShowModal("unlock_unlimited"); }} style={{ flex: 1, padding: "16px 12px", borderRadius: "14px", border: "none", background: C.teal, cursor: "pointer", textAlign: "center", color: "#fff", position: "relative" }}>
                      <div style={{ position: "absolute", top: "-10px", left: "50%", transform: "translateX(-50%)", fontSize: "10px", fontWeight: 700, color: "#fff", background: C.tealDark, padding: "3px 10px", borderRadius: "8px" }}>BEST VALUE</div>
                      <div style={{ fontSize: "13px", fontWeight: 600 }}>Unlimited</div><div style={{ fontSize: "24px", fontWeight: 800 }}>$10<span style={{ fontSize: "13px", fontWeight: 500 }}>/mo</span></div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.8); } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        textarea::placeholder { color: #9CA3AF; }
        input::placeholder { color: #9CA3AF; }
        button:hover { filter: brightness(0.97); }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { overflow-x: hidden; }

        /* ============================================================
           MOBILE RESPONSIVE
        ============================================================ */
        @media (max-width: 768px) {

          /* Nav */
          nav { padding: 0 12px !important; }
          nav > div { height: 70px !important; }
          nav img { height: 56px !important; }
          .nav-links { display: none !important; }

          /* Hero */
          .hero-section { padding: 24px 16px 0 !important; }
          .badge-row { flex-direction: column !important; align-items: center !important; gap: 8px !important; }

          /* Upload card */
          .upload-card-wrapper { padding: 0 12px !important; max-width: 100% !important; }

          /* All 3-column grids become 1 column */
          .stats-strip,
          .how-it-works > div:last-child,
          .testimonials-section div[class],
          .pricing-section div[class] { grid-template-columns: 1fr !important; }

          /* Stats strip specifics */
          .stats-strip { gap: 0 !important; }
          .stats-strip > div { padding: 10px !important; border-bottom: 1px solid #E5E7EB; }

          /* How it works */
          .how-it-works { padding: 32px 16px 20px !important; }

          /* Pricing */
          .pricing-section { padding: 32px 16px !important; }

          /* Testimonials */
          .testimonials-section { padding: 32px 16px !important; }

          /* Footer links */
          .footer-links { gap: 12px !important; flex-wrap: wrap !important; justify-content: center !important; }

          /* Mascot below form - hide on mobile to save space */
          img[alt="Penny Paws the Quote Bandit"] { max-width: 100% !important; }
        }

        @media (max-width: 480px) {
          h1 { letter-spacing: -0.5px !important; }
          nav img { height: 44px !important; }
          nav > div { height: 60px !important; }
        }
      `}</style>
    </div>
  );
}
